import { useEffect, useMemo, useState } from 'react';

import type { IdCompanyDto } from '@/services/ws-companies/src/main/resources/rest-api';
import { getCompanyVatRate, toDate } from '@/services/ws-companies/src/main/resources/rest-api';
import type { IdPostCampaignDto, IdTargetGroupDto, PostCampaignDto } from '@/services/ws-post/src/main/resources/rest-api';
import { fromDate, getInvoiceState, getPostCampaignById, InvoiceStateEnum } from '@/services/ws-post/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { useParams } from '@/js/libs/router';
import { useIdentityUser } from '@/js/libs/use-auth';
import { usePrevious } from '@/js/libs/use-previous';
import { NotActive, useRequest } from '@/js/libs/use-request';
import type { OnSuccess } from '@/js/libs/use-request-legacy';
import { useRequestLegacy } from '@/js/libs/use-request-legacy';

import { useRequestErrorNotification } from 'src/hooks/useRequestErrorNotification';
import { getPreviewDisabledReason } from 'src/scenes/PostCampaign/scenes/BookingTool/helpers';
import { checkAdUnitsImported, hasPositions } from '../helpers';
import type { PostCampaignRouteParams } from '../types';
import { Step } from '../types';

interface StepEntities {
  targetGroupId?: IdTargetGroupDto;
}

type StepData = Pick<
  PostCampaignDto,
  'targetGroup' | 'adUnitsImported' | 'hasMailingPositions' | 'hasDigitalPositions' | 'hasServicePositions'
>;

type StepEntityUpdateHandler = (data: Partial<StepData>) => void;

type PostCampaignUpdateHandler = (postCampaign: PostCampaignDto) => void;

const { usePostCampaignContext } = createContexts({
  PostCampaign() {
    const user = useIdentityUser();
    const companyId = user?.company.id;
    const { campaignId } = useParams<PostCampaignRouteParams>();
    const prevCampaignId = usePrevious(campaignId);

    const [postCampaign, setPostCampaign] = useState<PostCampaignDto | null>(null);

    const { targetGroup, info } = postCampaign ?? {};

    const hasAnyPosition = hasPositions(postCampaign);
    const adUnitsImported = checkAdUnitsImported(postCampaign);

    const [, { pending, error }, fetchCampaign] = useRequestLegacy({
      apiMethod: getPostCampaignById,
      parameters: [{ id: campaignId as IdPostCampaignDto }],
      active: !!campaignId,
      onSuccess: useCallbackRef<OnSuccess<getPostCampaignById>>((response) => setPostCampaign(response)),
    });

    const { data$: invoiceStateData$, pending$: invoiceStatePending$ } = useRequest({
      apiMethod: getInvoiceState,
      params: postCampaign ? { postCampaignId: postCampaign.id } : NotActive,
    });
    const invoiceState = useSubscribe(invoiceStateData$, (response) => response?.data);
    const invoiceStatePending = useSubscribe(invoiceStatePending$);

    const [countryVat, { pending: getCountryVatPending }] = useRequestLegacy({
      apiMethod: getCompanyVatRate,
      parameters: [
        {
          companyId: companyId as IdCompanyDto,
          country: postCampaign?.countryCode as string,
          date: postCampaign?.endDate ? toDate(fromDate(postCampaign?.endDate)) : undefined,
        },
      ],
      active: !!postCampaign?.countryCode && !!companyId,
      onFail: useRequestErrorNotification(),
    });

    const handleStepEntityUpdate = useCallbackRef<StepEntityUpdateHandler>((data) => {
      setPostCampaign((prevState) => {
        if (!prevState) {
          return prevState;
        }

        return {
          ...prevState,
          ...data,
        };
      });
    });

    const handlePostCampaignUpdate = useCallbackRef<PostCampaignUpdateHandler>((value) => setPostCampaign(value));

    const stepEntities = useMemo<StepEntities>(
      () => ({
        targetGroupId: targetGroup?.id,
      }),
      [targetGroup?.id],
    );

    const previewStepDisabled = !!getPreviewDisabledReason(postCampaign);

    const disabledSteps = useMemo<Step[]>(() => {
      const steps: (Step | false | undefined)[] = [
        !adUnitsImported && Step.CALCULATION,
        previewStepDisabled && Step.PREVIEW,
        !info && Step.TARGET_GROUP_SETTINGS,
        !hasAnyPosition && Step.PREVIEW,
      ];

      return steps.filter((item): item is Step => Boolean(item));
    }, [adUnitsImported, previewStepDisabled, info, hasAnyPosition]);

    useEffect(() => {
      if (prevCampaignId && !campaignId) {
        setPostCampaign(null);
      }
    }, [campaignId, prevCampaignId]);

    const isPositionLockedByInvoice = useMemo(
      () => invoiceState === InvoiceStateEnum.CHARGED || invoiceState === InvoiceStateEnum.MANUALLY_CHARGED,
      [invoiceState],
    );

    return {
      postCampaign,
      onStepEntityUpdate: handleStepEntityUpdate,
      pending: pending || getCountryVatPending || invoiceStatePending,
      stepEntities,
      onPostCampaignUpdate: handlePostCampaignUpdate,
      disabledSteps,
      fetchCampaign,
      error,
      hasAnyPosition,
      adUnitsImported,
      countryVat,
      invoiceState,
      isPositionLockedByInvoice,
    };
  },
});

export { usePostCampaignContext };
